import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  Clock,
  Copy,
  FileCode,
  GripVertical,
  Loader2,
  Mail,
  Globe,
  Pencil,
  Plus,
  Radio,
  Trash2,
  Users,
  Workflow,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import ConfirmModal from '../Common/ConfirmModal';
import TimezoneModal from '../Common/TimezoneModal';
import { supabase } from '../../lib/supabase';
import CancelAutomationEnrollmentModal from './CancelAutomationEnrollmentModal';
import EnrollAutomationModal from './EnrollAutomationModal';
import AutomationEnrollmentsTab, {
  type EnrollmentWithSubscriber,
} from './AutomationEnrollmentsTab';
import AutomationScheduledSendsTab from './AutomationScheduledSendsTab';
import AutomationStepEngagementTab from './AutomationStepEngagementTab';
import AutomationStepEditor from './AutomationStepEditor';
import PostmarkSignaturePicker from '../PostmarkSignatures/PostmarkSignaturePicker';
import {
  contentModeBadgeClass,
  contentModeBadgeLabel,
  inferContentMode,
} from './contentMode';
import type {
  AutomationRow,
  AutomationStatus,
  AutomationStepRow,
} from './types';
const STATUS_OPTIONS: AutomationStatus[] = ['draft', 'active', 'paused', 'archived'];

/** Same cadence and vocabulary as the step editor, so both forms feel identical. */
const AUTO_SAVE_DELAY_MS = 900;
const ALIAS_PATTERN = /^[a-z0-9_-]{2,64}$/;

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

function serializeHeaderForm(state: {
  name: string;
  alias: string;
  description: string;
  status: AutomationStatus;
  tz: string;
  postmarkSignatureId: number | null;
}): string {
  return JSON.stringify(state);
}

/** Card chrome on sm+; borderless on mobile (layout padding provides the inset). */
const PANEL_SHELL =
  'bg-white dark:bg-gray-800 min-w-0 overflow-hidden sm:rounded-xl sm:shadow sm:border sm:border-gray-200 sm:dark:border-gray-700';
const PANEL_SHELL_DANGER =
  'bg-white dark:bg-gray-800 min-w-0 overflow-hidden sm:rounded-xl sm:shadow sm:border sm:border-red-200 sm:dark:border-red-900/50';
const PANEL_PAD = 'px-4 sm:px-6';
const PANEL_BODY = 'px-4 py-4 sm:p-6';

type AutomationTab = 'overview' | 'enrollments' | 'engagement' | 'sends' | 'delete';

const AUTOMATION_TABS: readonly AutomationTab[] = [
  'overview',
  'enrollments',
  'engagement',
  'sends',
  'delete',
] as const;
const DEFAULT_AUTOMATION_TAB: AutomationTab = 'overview';

function parseAutomationTab(raw: string | null): AutomationTab {
  return AUTOMATION_TABS.includes(raw as AutomationTab)
    ? (raw as AutomationTab)
    : DEFAULT_AUTOMATION_TAB;
}

const STEP_QUERY_KEY = 'step';
const NEW_STEP_QUERY = 'new';
const VIEW_QUERY_KEY = 'view';

function describeSchedule(step: AutomationStepRow): string {
  const dayPart =
    step.day_offset === 0
      ? 'Same day'
      : step.day_offset === 1
        ? 'Next day'
        : `+${step.day_offset} days`;
  return `${dayPart} at ${step.time_of_day}`;
}

export default function AutomationDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [automation, setAutomation] = useState<AutomationRow | null>(null);
  const [steps, setSteps] = useState<AutomationStepRow[]>([]);
  const [enrollments, setEnrollments] = useState<EnrollmentWithSubscriber[]>([]);
  const [enrollmentsRealtime, setEnrollmentsRealtime] = useState(false);
  const [stepsRealtime, setStepsRealtime] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [headerSaveStatus, setHeaderSaveStatus] = useState<SaveStatus>('idle');
  const [headerSaveError, setHeaderSaveError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<AutomationStatus>('draft');
  const [tz, setTz] = useState('America/New_York');
  const [postmarkSignatureId, setPostmarkSignatureId] = useState<number | null>(null);

  const [tzModalOpen, setTzModalOpen] = useState(false);

  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<AutomationStepRow | null>(null);

  const [duplicateStep, setDuplicateStep] = useState<AutomationStepRow | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  const [reorderMode, setReorderMode] = useState(false);
  const [dragStepIdx, setDragStepIdx] = useState<number | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [cancelEnrollmentTarget, setCancelEnrollmentTarget] =
    useState<EnrollmentWithSubscriber | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [deleteAutomationConfirmOpen, setDeleteAutomationConfirmOpen] = useState(false);
  const [deletingAutomation, setDeletingAutomation] = useState(false);
  const tabScrollRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<Partial<Record<AutomationTab, HTMLButtonElement | null>>>(
    {},
  );

  /**
   * Two things a live refresh must not walk over: half-typed settings, and an
   * optimistic step order that has not finished persisting. Kept in a ref because the
   * realtime handler is registered once and would otherwise close over stale values.
   */
  const liveGuardRef = useRef({ dirty: false, reordering: false });

  /** Snapshot of the last state known to be in the database, for autosave. */
  const lastSavedHeaderRef = useRef('');
  const headerSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerSaveInFlightRef = useRef(false);

  const activeTab = parseAutomationTab(searchParams.get('tab'));
  const stepQuery = searchParams.get(STEP_QUERY_KEY);
  const setActiveTab = useCallback(
    (tab: AutomationTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === DEFAULT_AUTOMATION_TAB) next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setStepQuery = useCallback(
    (stepId: string | null) => {
      setSearchParams(
        (prev) => {
          const current = prev.get(STEP_QUERY_KEY);
          if (!stepId && current == null) return prev;
          if (stepId && current === stepId) return prev;
          const next = new URLSearchParams(prev);
          if (!stepId) {
            next.delete(STEP_QUERY_KEY);
            next.delete(VIEW_QUERY_KEY);
          } else {
            next.set(STEP_QUERY_KEY, stepId);
            // New steps start in edit. Prev/next keeps `view` so preview walks the series.
            if (stepId === NEW_STEP_QUERY) next.delete(VIEW_QUERY_KEY);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // On mobile, slide the selected tab toward the center of the strip.
  useEffect(() => {
    const container = tabScrollRef.current;
    const button = tabButtonRefs.current[activeTab];
    if (!container || !button) return;
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches) {
      return;
    }
    const cRect = container.getBoundingClientRect();
    const bRect = button.getBoundingClientRect();
    const delta = bRect.left + bRect.width / 2 - (cRect.left + cRect.width / 2);
    container.scrollBy({ left: delta, behavior: 'smooth' });
  }, [activeTab]);

  const loadEnrollments = useCallback(async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('automation_enrollments')
        .select('*, subscriber:subscribers(id, email, name, email_status, custom_data)')
        .eq('automation_id', id)
        .order('enrolled_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      setEnrollments((data ?? []) as EnrollmentWithSubscriber[]);
    } catch (e: unknown) {
      reportUiError('automations', 'load_enrollments', e, { automation_id: id });
    }
  }, [id]);

  const loadSteps = useCallback(async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('automation_steps')
        .select('*')
        .eq('automation_id', id)
        .order('position', { ascending: true });
      if (error) throw error;
      setSteps((data ?? []) as AutomationStepRow[]);
    } catch (e: unknown) {
      reportUiError('automation_steps', 'load_steps', e, { automation_id: id });
    }
  }, [id]);

  /** Fill the settings form from a row and mark that state as the saved baseline. */
  const seedHeaderForm = useCallback((row: AutomationRow) => {
    const signatureId =
      typeof row.postmark_signature_id === 'number' ? row.postmark_signature_id : null;
    setName(row.name);
    setAlias(row.alias ?? '');
    setDescription(row.description ?? '');
    setStatus(row.status);
    setTz(row.tz);
    setPostmarkSignatureId(signatureId);
    lastSavedHeaderRef.current = serializeHeaderForm({
      name: row.name,
      alias: row.alias ?? '',
      description: row.description ?? '',
      status: row.status,
      tz: row.tz,
      postmarkSignatureId: signatureId,
    });
    setHeaderSaveStatus('idle');
    setHeaderSaveError(null);
  }, []);

  /**
   * Header only. Re-seeds the settings form from the row unless there are unsaved
   * edits in it — someone typing an alias should not lose it because the cron touched
   * updated_at.
   */
  const refreshAutomation = useCallback(async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('automations')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return;
      const row = data as AutomationRow;
      setAutomation(row);
      if (liveGuardRef.current.dirty) return;
      seedHeaderForm(row);
    } catch (e: unknown) {
      reportUiError('automations', 'refresh', e, { automation_id: id });
    }
  }, [id, seedHeaderForm]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    try {
      const { data: a, error: aErr } = await supabase
        .from('automations')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (aErr) throw aErr;
      if (!a) {
        setNotFound(true);
        setAutomation(null);
        return;
      }
      const row = a as AutomationRow;
      setAutomation(row);
      seedHeaderForm(row);

      const [stepsRes, enrollsRes] = await Promise.all([
        supabase
          .from('automation_steps')
          .select('*')
          .eq('automation_id', id)
          .order('position', { ascending: true }),
        supabase
          .from('automation_enrollments')
          .select('*, subscriber:subscribers(id, email, name, email_status, custom_data)')
          .eq('automation_id', id)
          .order('enrolled_at', { ascending: false })
          .limit(200),
      ]);
      if (stepsRes.error) throw stepsRes.error;
      if (enrollsRes.error) throw enrollsRes.error;
      setSteps((stepsRes.data ?? []) as AutomationStepRow[]);
      setEnrollments((enrollsRes.data ?? []) as EnrollmentWithSubscriber[]);
    } catch (e: unknown) {
      reportUiError('automations', 'load', e, { automation_id: id });
      toast.error(e instanceof Error ? e.message : 'Failed to load automation');
      setAutomation(null);
    } finally {
      setLoading(false);
    }
  }, [id, seedHeaderForm]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The open step lives in `?step=` so refresh (and shared links) restore the editor.
   * Keep the in-memory row when the same id is still open — a live step reload must
   * not re-hydrate the form and wipe half-typed copy.
   */
  useEffect(() => {
    if (loading) return;

    if (!stepQuery) {
      setStepModalOpen(false);
      setEditingStep(null);
      return;
    }

    if (stepQuery === NEW_STEP_QUERY) {
      setEditingStep(null);
      setStepModalOpen(true);
      return;
    }

    const match = steps.find((s) => s.id === stepQuery);
    if (match) {
      setEditingStep((prev) => (prev?.id === match.id ? prev : match));
      setStepModalOpen(true);
      return;
    }

    // Already in the editor (create just swapped `?step=new` for a real id, and
    // the list has not caught up). Leave it. Otherwise drop a stale deep link.
    if (stepModalOpen) return;
    setStepQuery(null);
  }, [loading, stepQuery, steps, stepModalOpen, setStepQuery]);

  // Live enrollments: realtime + poll while Enrollments tab is visible.
  const loadEnrollmentsRef = useRef(loadEnrollments);
  useEffect(() => {
    loadEnrollmentsRef.current = loadEnrollments;
  }, [loadEnrollments]);

  useEffect(() => {
    if (!id) return;
    setEnrollmentsRealtime(false);
    const channel = supabase
      .channel(`automation_enrollments:automation:${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automation_enrollments',
          filter: `automation_id=eq.${id}`,
        },
        () => {
          void loadEnrollmentsRef.current();
        },
      )
      .subscribe((status) => {
        setEnrollmentsRealtime(status === 'SUBSCRIBED');
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [id]);

  // Live steps and settings. Steps are written from outside this tab — Engine MCP
  // creates and rewrites them from Cursor and the hosted connector — so the open page
  // has no other way to find out. Debounced because a reorder writes every row twice.
  const loadStepsRef = useRef(loadSteps);
  const refreshAutomationRef = useRef(refreshAutomation);
  useEffect(() => {
    loadStepsRef.current = loadSteps;
    refreshAutomationRef.current = refreshAutomation;
  }, [loadSteps, refreshAutomation]);

  useEffect(() => {
    if (!id) return;
    setStepsRealtime(false);

    let timer: number | undefined;
    const reloadSteps = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        // Mid-reorder the local order is the truth until it persists; refetching now
        // would snap rows back to the pre-drop positions.
        if (liveGuardRef.current.reordering) return;
        void loadStepsRef.current();
      }, 300);
    };

    const channel = supabase
      .channel(`automation_detail:${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'automation_steps',
          filter: `automation_id=eq.${id}`,
        },
        reloadSteps,
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'automations',
          filter: `id=eq.${id}`,
        },
        () => {
          void refreshAutomationRef.current();
        },
      )
      .subscribe((status) => {
        setStepsRealtime(status === 'SUBSCRIBED');
      });

    return () => {
      if (timer) window.clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (activeTab !== 'enrollments') return;

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void loadEnrollmentsRef.current();
    };
    const intervalMs = enrollmentsRealtime ? 60_000 : 15_000;
    const handle = window.setInterval(tick, intervalMs);

    const onVisibility = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [id, activeTab, enrollmentsRealtime]);

  useEffect(() => {
    if (steps.length < 2 && reorderMode) {
      exitReorderMode();
    }
  }, [steps.length, reorderMode]);

  const nextPosition = useMemo(() => {
    if (steps.length === 0) return 1;
    return Math.max(...steps.map((s) => s.position)) + 1;
  }, [steps]);

  const activeEnrollmentCount = useMemo(
    () => enrollments.filter((e) => e.status === 'active').length,
    [enrollments],
  );

  const activeEnrollmentSubscriberIds = useMemo(
    () =>
      enrollments
        .filter((e) => e.status === 'active')
        .map((e) => e.subscriber_id),
    [enrollments],
  );

  const headerDirty =
    automation != null &&
    (name !== automation.name ||
      alias !== (automation.alias ?? '') ||
      description !== (automation.description ?? '') ||
      status !== automation.status ||
      tz !== automation.tz ||
      postmarkSignatureId !==
        (typeof automation.postmark_signature_id === 'number'
          ? automation.postmark_signature_id
          : null));

  useEffect(() => {
    liveGuardRef.current = {
      dirty: headerDirty,
      reordering: reorderMode || savingOrder,
    };
  }, [headerDirty, reorderMode, savingOrder]);

  const headerSnapshot = useMemo(
    () =>
      serializeHeaderForm({ name, alias, description, status, tz, postmarkSignatureId }),
    [name, alias, description, status, tz, postmarkSignatureId],
  );

  /**
   * Autosave for the settings form. Silent — the status line under the heading is the
   * only feedback, because a toast per keystroke pause is noise.
   *
   * Invalid input parks in `error` rather than writing: an empty name would be
   * rejected by the column anyway, and a malformed alias would break enrollment by
   * external trigger.
   */
  const persistHeader = useCallback(async () => {
    const automationId = automation?.id;
    if (!automationId) return;
    if (headerSaveInFlightRef.current) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setHeaderSaveStatus('error');
      setHeaderSaveError('Name is required');
      return;
    }
    const aliasTrim = alias.trim().toLowerCase();
    if (aliasTrim && !ALIAS_PATTERN.test(aliasTrim)) {
      setHeaderSaveStatus('error');
      setHeaderSaveError(
        'Alias must be 2–64 chars, lowercase letters, digits, hyphens, or underscores',
      );
      return;
    }

    const patch = {
      name: trimmedName,
      alias: aliasTrim || null,
      description: description.trim() || null,
      status,
      tz: tz.trim() || 'America/New_York',
      postmark_signature_id: postmarkSignatureId,
    };
    const snapshotAtSave = headerSnapshot;

    headerSaveInFlightRef.current = true;
    setHeaderSaveStatus('saving');
    setHeaderSaveError(null);
    try {
      const { error } = await supabase
        .from('automations')
        .update(patch)
        .eq('id', automationId);
      if (error) throw error;
      lastSavedHeaderRef.current = snapshotAtSave;
      // Fold the patch into the row locally so the dirty check clears immediately;
      // the realtime UPDATE that follows is then a no-op rather than a re-seed.
      setAutomation((prev) => (prev ? { ...prev, ...patch } : prev));
      setHeaderSaveStatus('saved');
    } catch (e: unknown) {
      reportUiError('automations', 'update', e, { automation_id: automationId });
      setHeaderSaveStatus('error');
      setHeaderSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      headerSaveInFlightRef.current = false;
    }
  }, [
    alias,
    automation?.id,
    description,
    headerSnapshot,
    name,
    postmarkSignatureId,
    status,
    tz,
  ]);

  useEffect(() => {
    if (!automation) return;
    if (headerSnapshot === lastSavedHeaderRef.current) return;

    setHeaderSaveStatus('pending');
    if (headerSaveTimerRef.current) clearTimeout(headerSaveTimerRef.current);
    headerSaveTimerRef.current = setTimeout(() => {
      headerSaveTimerRef.current = null;
      void persistHeader();
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (headerSaveTimerRef.current) {
        clearTimeout(headerSaveTimerRef.current);
        headerSaveTimerRef.current = null;
      }
    };
  }, [automation, headerSnapshot, persistHeader]);

  const headerSaveLabel = (() => {
    switch (headerSaveStatus) {
      case 'pending':
        return 'Unsaved changes';
      case 'saving':
        return 'Saving…';
      case 'saved':
        return 'Saved';
      case 'error':
        return headerSaveError ?? 'Save failed';
      default:
        return null;
    }
  })();

  const handleDeleteAutomation = async () => {
    if (!automation) return;
    setDeletingAutomation(true);
    try {
      const { error } = await supabase.from('automations').delete().eq('id', automation.id);
      if (error) throw error;
      setDeleteAutomationConfirmOpen(false);
      toast.success('Automation deleted');
      navigate('/automations');
    } catch (e: unknown) {
      reportUiError('automations', 'delete', e, { automation_id: automation.id });
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeletingAutomation(false);
    }
  };

  const openDuplicateStep = (s: AutomationStepRow) => {
    setDuplicateStep(s);
    const baseLabel = s.name?.trim() || s.subject?.trim() || `Step ${s.position}`;
    setDuplicateName(`${baseLabel} (copy)`);
  };

  const closeDuplicateStep = () => {
    if (duplicating) return;
    setDuplicateStep(null);
    setDuplicateName('');
  };

  const persistStepOrder = async (ordered: AutomationStepRow[]) => {
    if (!automation) return;
    setSavingOrder(true);
    const tempOffset = 100_000;
    try {
      const phase1 = await Promise.all(
        ordered.map((step, i) =>
          supabase
            .from('automation_steps')
            .update({ position: tempOffset + i })
            .eq('id', step.id),
        ),
      );
      const phase1Err = phase1.find((r) => r.error)?.error;
      if (phase1Err) throw phase1Err;

      const phase2 = await Promise.all(
        ordered.map((step, i) =>
          supabase
            .from('automation_steps')
            .update({ position: i + 1 })
            .eq('id', step.id),
        ),
      );
      const phase2Err = phase2.find((r) => r.error)?.error;
      if (phase2Err) throw phase2Err;

      setSteps(ordered.map((s, i) => ({ ...s, position: i + 1 })));
      toast.success('Step order updated');
    } catch (e: unknown) {
      reportUiError('automation_steps', 'reorder', e, { automation_id: automation.id });
      toast.error(e instanceof Error ? e.message : 'Failed to update step order');
      void load();
    } finally {
      setSavingOrder(false);
    }
  };

  const moveStep = (from: number, to: number) => {
    if (from === to) return;
    if (from < 0 || to < 0 || from >= steps.length || to >= steps.length) return;
    const next = [...steps];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    const reordered = next.map((s, i) => ({ ...s, position: i + 1 }));
    setSteps(reordered);
    void persistStepOrder(reordered);
  };

  const exitReorderMode = () => {
    setReorderMode(false);
    setDragStepIdx(null);
  };

  const handleConfirmDuplicateStep = async () => {
    if (!automation || !duplicateStep) return;
    const trimmedName = duplicateName.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    setDuplicating(true);
    try {
      const s = duplicateStep;
      const mode = inferContentMode(s);
      const insertPayload = {
        automation_id: automation.id,
        position: nextPosition,
        day_offset: s.day_offset,
        time_of_day: s.time_of_day,
        name: trimmedName,
        content_mode: mode,
        subject: mode === 'template_only' ? null : s.subject,
        body_html:
          mode === 'manual' || mode === 'template_wrap' ? s.body_html : null,
        postmark_template_alias:
          mode === 'manual' ? null : s.postmark_template_alias,
        active: s.active !== false,
        personalization_config: s.personalization_config ?? {
          enabled: false,
          min_score: 50,
        },
      };
      const { error } = await supabase.from('automation_steps').insert(insertPayload);
      if (error) throw error;
      toast.success('Step duplicated');
      setDuplicateStep(null);
      setDuplicateName('');
      void load();
    } catch (e: unknown) {
      reportUiError('automation_steps', 'duplicate', e, {
        automation_id: automation.id,
        step_id: duplicateStep.id,
      });
      toast.error(e instanceof Error ? e.message : 'Duplicate failed');
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto w-full min-w-0 overflow-x-hidden">
      <div className="mb-6">
        <Link
          to="/automations"
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Automations
        </Link>
      </div>

      {loading ? (
        <div className={`${PANEL_SHELL} p-12 flex justify-center`}>
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      ) : notFound || !automation ? (
        <div className={`${PANEL_SHELL} p-8 text-center`}>
          <p className="text-gray-700 dark:text-gray-200">Automation not found.</p>
          <Link
            to="/automations"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to list
          </Link>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6 min-w-0">
          <CancelAutomationEnrollmentModal
            isOpen={cancelEnrollmentTarget != null}
            enrollment={cancelEnrollmentTarget}
            automationName={automation.name}
            onClose={() => setCancelEnrollmentTarget(null)}
            onSuccess={() => void load()}
          />

          <EnrollAutomationModal
            isOpen={enrollOpen}
            automationId={automation.id}
            automationName={automation.name}
            automationStatus={automation.status}
            excludeSubscriberIds={activeEnrollmentSubscriberIds}
            onClose={() => setEnrollOpen(false)}
            onSuccess={() => {
              setActiveTab('enrollments');
              void load();
            }}
          />

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Workflow className="w-8 h-8 mr-3 text-emerald-500 shrink-0" />
                <span className="truncate">{automation.name || '(untitled)'}</span>
              </h1>
              {automation.alias ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-mono">
                  alias: <span className="text-gray-700 dark:text-gray-200">{automation.alias}</span>
                </p>
              ) : null}
            </div>
          </div>

          <div
            ref={tabScrollRef}
            className="w-full min-w-0 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div
              className="inline-flex items-center min-w-max bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700"
              role="tablist"
              aria-label="Automation sections"
            >
              <button
                type="button"
                role="tab"
                ref={(el) => {
                  tabButtonRefs.current.overview = el;
                }}
                aria-selected={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
                className={`whitespace-nowrap px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Overview
              </button>
              <button
                type="button"
                role="tab"
                ref={(el) => {
                  tabButtonRefs.current.enrollments = el;
                }}
                aria-selected={activeTab === 'enrollments'}
                onClick={() => setActiveTab('enrollments')}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'enrollments'
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Enrollments
                {enrollments.length > 0 ? (
                  <span
                    title={
                      activeEnrollmentCount > 0
                        ? `${activeEnrollmentCount} active · ${enrollments.length} total`
                        : `${enrollments.length} total`
                    }
                    className={`ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      activeTab === 'enrollments'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                    }`}
                  >
                    {enrollments.length}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                role="tab"
                ref={(el) => {
                  tabButtonRefs.current.engagement = el;
                }}
                aria-selected={activeTab === 'engagement'}
                onClick={() => setActiveTab('engagement')}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'engagement'
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5" />
                Engagement
              </button>
              <button
                type="button"
                role="tab"
                ref={(el) => {
                  tabButtonRefs.current.sends = el;
                }}
                aria-selected={activeTab === 'sends'}
                onClick={() => setActiveTab('sends')}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'sends'
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                Scheduled sends
              </button>
              <button
                type="button"
                role="tab"
                ref={(el) => {
                  tabButtonRefs.current.delete = el;
                }}
                aria-selected={activeTab === 'delete'}
                onClick={() => setActiveTab('delete')}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'delete'
                    ? 'bg-white dark:bg-gray-900 text-red-700 dark:text-red-300 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-300'
                }`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          </div>

          {activeTab === 'engagement' ? (
            <AutomationStepEngagementTab steps={steps} automationTz={automation.tz} />
          ) : null}

          {activeTab === 'enrollments' ? (
            <AutomationEnrollmentsTab
              enrollments={enrollments}
              automationStatus={automation.status}
              canEnroll={automation.status === 'active'}
              live={enrollmentsRealtime}
              onEnroll={() => setEnrollOpen(true)}
              onRequestCancel={setCancelEnrollmentTarget}
            />
          ) : null}

          {activeTab === 'sends' ? (
            <section className={`${PANEL_SHELL} ${PANEL_BODY}`}>
              <AutomationScheduledSendsTab
                automationId={automation.id}
                automationName={automation.name}
                steps={steps}
              />
            </section>
          ) : null}

          {activeTab === 'delete' ? (
            <section className={PANEL_SHELL_DANGER}>
              <div className={`${PANEL_PAD} py-4 border-b border-red-200 dark:border-red-900/50 bg-red-50/60 dark:bg-red-950/20`}>
                <h2 className="text-sm font-semibold text-red-800 dark:text-red-200 uppercase tracking-wide">
                  Delete automation
                </h2>
              </div>
              <div className={`${PANEL_BODY} space-y-4`}>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed max-w-2xl">
                  Permanently remove{' '}
                  <span className="font-medium text-gray-900 dark:text-white">{automation.name}</span>,
                  all of its steps, enrollments, and pending scheduled sends. Sent email history may
                  remain in logs but this automation cannot be recovered.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc pl-5 space-y-1 max-w-2xl">
                  <li>Active enrollments will be canceled</li>
                  <li>Pending scheduled emails for this automation will be removed</li>
                  <li>Step definitions and settings will be deleted</li>
                </ul>
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setDeleteAutomationConfirmOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete this automation…
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          {activeTab === 'overview' ? (
          <>
          {/* Header editor */}
          <section className={PANEL_SHELL}>
            <div className={`${PANEL_PAD} py-4 border-b border-gray-200 dark:border-gray-700 flex items-baseline justify-between gap-3`}>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                Settings
              </h2>
              <div className="flex items-baseline gap-3 min-w-0">
                {headerSaveLabel ? (
                  <p
                    className={`text-xs truncate ${
                      headerSaveStatus === 'error'
                        ? 'text-red-600 dark:text-red-400'
                        : headerSaveStatus === 'saved'
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-gray-500 dark:text-gray-400'
                    }`}
                    role={headerSaveStatus === 'error' ? 'alert' : 'status'}
                  >
                    {headerSaveLabel}
                  </p>
                ) : null}
                {/* Set once per automation, so it reads as a label until you click it. */}
                <button
                  type="button"
                  onClick={() => setTzModalOpen(true)}
                  title="Change timezone — step times of day are interpreted in this zone"
                  className="inline-flex items-center gap-1 shrink-0 text-xs font-mono text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:underline"
                >
                  <Globe className="w-3.5 h-3.5 text-gray-400" />
                  {tz}
                </button>
              </div>
            </div>
            <div className={`${PANEL_BODY} space-y-4`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    placeholder="Internal notes about this automation"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Alias{' '}
                    <span className="text-gray-400 dark:text-gray-500 font-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={alias}
                    onChange={(e) =>
                      setAlias(e.target.value.toLowerCase().replace(/\s+/g, '-'))
                    }
                    placeholder="e.g. welcome-series"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Lowercase letters, digits, hyphens, underscores. Used to enroll
                    subscribers from external triggers (webhooks, integrations).
                  </p>
                </div>
                <div className="lg:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as AutomationStatus)}
                    className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Only <code className="font-mono">active</code> enrolls.
                  </p>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <PostmarkSignaturePicker
                    value={postmarkSignatureId}
                    onChange={setPostmarkSignatureId}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Steps */}
          <section className={PANEL_SHELL}>
            <div className={`${PANEL_PAD} py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap`}>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                  Steps
                </h2>
                {steps.length > 0 ? (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {steps.length} step{steps.length === 1 ? '' : 's'}
                  </span>
                ) : null}
                {stepsRealtime ? (
                  <span
                    title="Live updates connected"
                    className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                  >
                    <Radio className="w-3 h-3" />
                    Live
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {steps.length >= 2 ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (reorderMode) {
                        exitReorderMode();
                      } else {
                        setStepQuery(null);
                        setReorderMode(true);
                      }
                    }}
                    disabled={savingOrder}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors disabled:opacity-50 ${
                      reorderMode
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-600'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    {savingOrder ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <GripVertical className="w-3.5 h-3.5" />
                    )}
                    {reorderMode ? 'Done reordering' : 'Edit order'}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setStepQuery(NEW_STEP_QUERY);
                  }}
                  disabled={reorderMode}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add step
                </button>
              </div>
            </div>

            {reorderMode ? (
              <p className={`${PANEL_PAD} py-2 text-xs text-emerald-800 dark:text-emerald-200 bg-emerald-50/80 dark:bg-emerald-900/20 border-b border-emerald-100 dark:border-emerald-900/40`}>
                Drag rows to change step order. Changes save when you drop a row.
              </p>
            ) : null}

            {steps.length === 0 ? (
              <div className={`${PANEL_PAD} py-10 flex flex-col items-center gap-3 text-center`}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No steps yet. Add the first email for this automation.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setStepQuery(NEW_STEP_QUERY);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4" />
                  Add a step
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-14">
                        {reorderMode ? '' : '#'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Step
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Schedule
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      {!reorderMode ? (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {steps.map((s, idx) => {
                      const isInactive = s.active === false;
                      const isDragging = dragStepIdx === idx;
                      return (
                        <tr
                          key={s.id}
                          draggable={reorderMode && !savingOrder}
                          onDragStart={() => {
                            if (!reorderMode || savingOrder) return;
                            setDragStepIdx(idx);
                          }}
                          onDragEnd={() => setDragStepIdx(null)}
                          onDragOver={(e) => {
                            if (!reorderMode || savingOrder) return;
                            e.preventDefault();
                          }}
                          onDrop={() => {
                            if (!reorderMode || savingOrder || dragStepIdx == null) return;
                            moveStep(dragStepIdx, idx);
                            setDragStepIdx(null);
                          }}
                          onClick={() => {
                            if (reorderMode) return;
                            setStepQuery(s.id);
                          }}
                          onKeyDown={(e) => {
                            if (reorderMode) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setStepQuery(s.id);
                            }
                          }}
                          role={reorderMode ? undefined : 'button'}
                          tabIndex={reorderMode ? undefined : 0}
                          className={`transition-colors ${
                            reorderMode
                              ? `cursor-grab active:cursor-grabbing ${
                                  isDragging ? 'opacity-50 bg-emerald-50/50 dark:bg-emerald-900/20' : ''
                                } hover:bg-gray-50 dark:hover:bg-gray-900/30`
                              : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-900/30'
                          } ${isInactive ? 'opacity-60' : ''}`}
                        >
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 font-mono whitespace-nowrap">
                            {reorderMode ? (
                              <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                                <GripVertical className="w-4 h-4 shrink-0" aria-hidden />
                                <span>#{idx + 1}</span>
                              </span>
                            ) : (
                              `#${s.position}`
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[28rem]">
                              {s.name || s.subject || '(unnamed step)'}
                            </div>
                            {s.name && s.subject ? (
                              <div
                                className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[28rem]"
                                title={s.subject}
                              >
                                {s.subject}
                              </div>
                            ) : null}
                            {(() => {
                              const mode = inferContentMode(s);
                              return (
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <span
                                    className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide border ${contentModeBadgeClass(mode)}`}
                                  >
                                    {contentModeBadgeLabel(mode)}
                                  </span>
                                  {s.postmark_template_alias ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
                                      <FileCode className="w-3 h-3" />
                                      {s.postmark_template_alias}
                                    </span>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              {describeSchedule(s)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            {isInactive ? (
                              <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                inactive
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                                active
                              </span>
                            )}
                          </td>
                          {!reorderMode ? (
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStepQuery(s.id);
                                }}
                                className="p-1 text-gray-400 hover:text-emerald-500 transition-colors"
                                title="Edit step"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          </>
          ) : null}
        </div>
      )}

      {automation ? (
        <AutomationStepEditor
          isOpen={stepModalOpen}
          onClose={() => setStepQuery(null)}
          automationId={automation.id}
          existing={editingStep}
          allSteps={steps}
          onNavigateStep={(stepId) => {
            setStepQuery(stepId);
          }}
          nextPosition={nextPosition}
          onSuccess={(savedStepId) => {
            void load();
            if (savedStepId && stepQuery === NEW_STEP_QUERY) {
              setStepQuery(savedStepId);
            }
          }}
          onDuplicate={
            editingStep
              ? () => {
                  const target = editingStep;
                  setStepQuery(null);
                  openDuplicateStep(target);
                }
              : undefined
          }
        />
      ) : null}

      <TimezoneModal
        isOpen={tzModalOpen}
        value={tz}
        onClose={() => setTzModalOpen(false)}
        onSelect={setTz}
        description="IANA name. Each step's time of day is interpreted in this zone, so changing it moves every future send for this automation."
      />

      {duplicateStep ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeDuplicateStep}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Copy className="w-5 h-5 text-emerald-500" />
                Duplicate step
              </h2>
              <button
                onClick={closeDuplicateStep}
                disabled={duplicating}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Creates a copy of{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {duplicateStep.name || duplicateStep.subject || `Step ${duplicateStep.position}`}
                </span>{' '}
                with the same schedule and content. The new step is appended at the end
                (position{' '}
                <span className="font-mono">{nextPosition}</span>) and marked{' '}
                <span className="font-medium">
                  {duplicateStep.active === false ? 'inactive' : 'active'}
                </span>
                .
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New step name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={duplicateName}
                  onChange={(e) => setDuplicateName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !duplicating) {
                      e.preventDefault();
                      void handleConfirmDuplicateStep();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeDuplicateStep}
                disabled={duplicating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmDuplicateStep()}
                disabled={duplicating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
              >
                {duplicating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {duplicating ? 'Duplicating…' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={deleteAutomationConfirmOpen}
        title="Delete automation?"
        description={
          automation ? (
            <>
              Permanently delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">{automation.name}</span>.
              Active enrollments will be canceled and pending scheduled emails removed. This cannot
              be undone.
            </>
          ) : null
        }
        confirmLabel="Delete"
        variant="danger"
        busy={deletingAutomation}
        onConfirm={() => void handleDeleteAutomation()}
        onClose={() => {
          if (!deletingAutomation) setDeleteAutomationConfirmOpen(false);
        }}
      />
    </div>
  );
}
